import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useOrganizationContext } from '../organization.context';
import { uploadAsset } from '@/lib/assets';
import { Input } from '@/components/ui/input';
import organizationApiPrivate, { updateOrganization } from '@/api/organization.api.private';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ASSETS_TYPES } from '@/constants/assets.constants';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { FEATURES } from 'shared/constants/features.constants';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ORGANIZATIONS_LOGO_URL, ORGANIZATIONS_HUB_LOGO_URL } from 'shared/constants';

const schema = z
  .object({
    hubAccessEnabled: z.boolean(),
    hubSharedEnabled: z.boolean(),
    isSubscribedToPrivateNetwork: z.boolean(),
    isPublicContentProvider: z.boolean(),
    healthSystem: z.string(),
    department: z.string().optional(),
    hospital: z.string(),
  })
  .refine(data => !data.hubSharedEnabled || data.healthSystem.length >= 2, {
    message: 'Health System is required',
    path: ['healthSystem'],
  });

export default function HubDetails() {
  const { setOrganization, organization } = useOrganizationContext();
  const [loading, setLoading] = useState(false);
  const [updateTimestamp, setUpdateTimestamp] = useState(null);
  const { toast } = useToast();

  const form = useForm({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      hubAccessEnabled: organization?.features?.includes(FEATURES.KNOWLEDGE_HUB_ACCESS) ?? false,
      hubSharedEnabled: organization?.features?.includes(FEATURES.KNOWLEDGE_HUB_SHARED) ?? false,
      isSubscribedToPrivateNetwork:
        organization?.configuration?.hub?.isSubscribedToPrivateNetwork ?? false,
      isPublicContentProvider: organization?.configuration?.hub?.isPublicContentProvider ?? false,
      healthSystem: organization?.healthSystem || '',
      department: organization?.department || '',
      hospital: organization?.hospital || '',
    },
  });

  const { isDirty, isValid } = form.formState;
  const hubAccessEnabled = form.watch('hubAccessEnabled');
  const hubSharedEnabled = form.watch('hubSharedEnabled');

  // Re-validate when hubSharedEnabled changes to update healthSystem/department requirements
  React.useEffect(() => {
    form.trigger(['healthSystem', 'department']);
  }, [hubSharedEnabled, form]);

  const toggleFeature = async (feature, enable) => {
    if (enable) {
      return organizationApiPrivate.activateFeature({ feature });
    }
    return organizationApiPrivate.deactivateFeature({ feature });
  };

  const onSubmit = async formData => {
    setLoading(true);

    const {
      hubAccessEnabled,
      hubSharedEnabled,
      isPublicContentProvider,
      isSubscribedToPrivateNetwork,
      ...restData
    } = formData;

    // Get current feature states
    const currentHubAccess =
      organization?.features?.includes(FEATURES.KNOWLEDGE_HUB_ACCESS) ?? false;
    const currentHubShared =
      organization?.features?.includes(FEATURES.KNOWLEDGE_HUB_SHARED) ?? false;

    // Toggle features if changed
    if (hubAccessEnabled !== currentHubAccess) {
      const { error } = await toggleFeature(FEATURES.KNOWLEDGE_HUB_ACCESS, hubAccessEnabled);
      if (error) {
        setLoading(false);
        return toast({
          title: 'Error updating Hub Access feature',
          variant: 'destructive',
        });
      }
    }

    if (hubSharedEnabled !== currentHubShared) {
      const { error } = await toggleFeature(FEATURES.KNOWLEDGE_HUB_SHARED, hubSharedEnabled);
      if (error) {
        setLoading(false);
        return toast({
          title: 'Error updating Hub Share feature',
          variant: 'destructive',
        });
      }
    }

    // Update organization data
    const payload = {
      ...restData,
      configuration: {
        ...organization?.configuration,
        hub: {
          ...organization?.configuration?.hub,
          isPublicContentProvider,
          isSubscribedToPrivateNetwork,
        },
      },
    };

    const { data, error } = await updateOrganization({
      data: payload,
      id: organization.id,
    });

    setLoading(false);

    if (error) {
      return toast({
        title: 'Error updating Knowledge Hub',
        description: 'An error occurred while updating Knowledge Hub settings',
        variant: 'destructive',
      });
    }

    setOrganization(data);

    form.reset({
      hubAccessEnabled: data.features?.includes(FEATURES.KNOWLEDGE_HUB_ACCESS) ?? false,
      hubSharedEnabled: data.features?.includes(FEATURES.KNOWLEDGE_HUB_SHARED) ?? false,
      isSubscribedToPrivateNetwork: data.configuration?.hub?.isSubscribedToPrivateNetwork ?? false,
      isPublicContentProvider: data.configuration?.hub?.isPublicContentProvider ?? false,
      healthSystem: data.healthSystem || '',
      department: data.department || '',
      hospital: data.hospital || '',
    });

    toast({
      title: 'Knowledge Hub has been updated',
    });
  };

  const [hubLogoFile, setHubLogoFile] = useState(null);
  const [hubLogoLoading, setHubLogoLoading] = useState(false);

  const setNewImage = event => {
    const file = event.target.files[0];
    if (file) {
      setHubLogoFile(file);
    }
  };

  const handleHubLogoUpload = async () => {
    if (!hubLogoFile) return;

    setHubLogoLoading(true);

    try {
      await uploadAsset(hubLogoFile, ASSETS_TYPES.KNOWLEDGE_HUB_LOGO, organization.logo);

      const timestamp = Date.now();

      const { data, error } = await updateOrganization({
        data: {
          configuration: {
            ...organization?.configuration,
            hub: {
              ...organization?.configuration?.hub,
              hubLogoUpdatedAt: timestamp,
            },
          },
        },
        id: organization.id,
      });

      if (error) {
        throw new Error(error.message || 'Failed to update organization metadata');
      }

      setOrganization(data);
      setUpdateTimestamp(timestamp);

      toast({
        title: 'Knowledge Hub Logo has been updated',
      });

      setHubLogoFile(null);
    } catch (error) {
      console.error('Error uploading hub logo:', error);
      toast({
        title: 'Error uploading logo',
        description: error.message || 'An error occurred while uploading',
        variant: 'destructive',
      });
    } finally {
      setHubLogoLoading(false);
    }
  };

  // Use persistent cache-busting from database so it survives page refreshes
  // Fall back to updateTimestamp for immediate UI feedback, then to hubLogoUpdatedAt
  const hubLogoUpdatedAt = organization?.configuration?.hub?.hubLogoUpdatedAt;
  const hasHubLogo = !!(updateTimestamp || hubLogoUpdatedAt);
  const cacheBuster = updateTimestamp
    ? `?v=${updateTimestamp}`
    : hubLogoUpdatedAt
      ? `?v=${hubLogoUpdatedAt}`
      : '';
  const logo = hasHubLogo
    ? `${ORGANIZATIONS_HUB_LOGO_URL}${organization.logo}${cacheBuster}`
    : `${ORGANIZATIONS_LOGO_URL}web/${organization.logo}`;
  const fallbackLogo = `${ORGANIZATIONS_LOGO_URL}web/${organization.logo}`;

  return (
    <TooltipProvider>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Knowledge Hub Settings</CardTitle>
          <Button
            type="submit"
            form="hub-details-form"
            loading={loading}
            disabled={loading || !isDirty || !isValid}>
            Save
          </Button>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              id="hub-details-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6 w-full">
              {/* Hub Logo Section */}
              <div className="space-y-2">
                <h3 className="text-lg font-medium">Hub Logo</h3>
                <p className="text-sm text-muted-foreground">
                  Custom logo for the Knowledge Hub. Falls back to the organization logo if not set.
                </p>
                <Input
                  id="webImage"
                  type="file"
                  name="webImage"
                  accept="image/png, image/jpeg"
                  onChange={e => setNewImage(e)}
                />
                <div className="mt-4">
                  <p className="text-sm font-medium">Preview</p>
                  <div className="p-3 rounded-md">
                    <img
                      key={updateTimestamp ?? hubLogoUpdatedAt}
                      alt="Hub Logo Preview"
                      src={hubLogoFile ? URL.createObjectURL(hubLogoFile) : logo}
                      onError={event => {
                        event.target.src = fallbackLogo;
                        event.onerror = null;
                      }}
                      width={105}
                      height={105}
                      className="rounded-md"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    type="button"
                    disabled={!hubLogoFile}
                    loading={hubLogoLoading}
                    onClick={handleHubLogoUpload}>
                    Upload
                  </Button>
                </div>
              </div>

              <Separator className="my-6 h-[2px]" />

              {/* Hub Access Section */}
              <div className="space-y-4 pb-6 border-b">
                <h3 className="text-lg font-medium">Hub Access</h3>
                <FormField
                  control={form.control}
                  name="hubAccessEnabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Hub Access</FormLabel>
                        <FormDescription>
                          Enable Knowledge Hub access for this organization
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <FormField
                        control={form.control}
                        name="isSubscribedToPrivateNetwork"
                        render={({ field }) => (
                          <FormItem
                            className={`flex flex-row items-center justify-between rounded-lg border p-4 ${
                              !hubAccessEnabled ? 'opacity-50' : ''
                            }`}>
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">
                                Subscribe to Private Network
                              </FormLabel>
                              <FormDescription>
                                Allow this organization to access private shared content from other
                                organizations in the hub
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={!hubAccessEnabled}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </TooltipTrigger>
                  {!hubAccessEnabled && (
                    <TooltipContent>
                      <p>Enable Hub Access to configure this setting</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </div>

              {/* Hub Share Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Hub Share</h3>
                <FormField
                  control={form.control}
                  name="hubSharedEnabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Hub Share</FormLabel>
                        <FormDescription>
                          Allow this organization to share content to the Knowledge Hub
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <FormField
                        control={form.control}
                        name="isPublicContentProvider"
                        render={({ field }) => (
                          <FormItem
                            className={`flex flex-row items-center justify-between rounded-lg border p-4 ${
                              !hubSharedEnabled ? 'opacity-50' : ''
                            }`}>
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Public Content Provider</FormLabel>
                              <FormDescription>
                                Make this organization&apos;s shared content visible to all users in
                                the Knowledge Hub
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={!hubSharedEnabled}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </TooltipTrigger>
                  {!hubSharedEnabled && (
                    <TooltipContent>
                      <p>Enable Hub Share to configure this setting</p>
                    </TooltipContent>
                  )}
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <FormField
                        control={form.control}
                        name="healthSystem"
                        render={({ field }) => (
                          <FormItem className={!hubSharedEnabled ? 'opacity-50' : ''}>
                            <FormLabel>
                              Health System{' '}
                              {hubSharedEnabled && <span className="text-red-500">*</span>}
                            </FormLabel>
                            <FormControl>
                              <Input {...field} disabled={!hubSharedEnabled} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </TooltipTrigger>
                  {!hubSharedEnabled && (
                    <TooltipContent>
                      <p>Enable Hub Share to configure this setting</p>
                    </TooltipContent>
                  )}
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <FormField
                        control={form.control}
                        name="department"
                        render={({ field }) => (
                          <FormItem className={!hubSharedEnabled ? 'opacity-50' : ''}>
                            <FormLabel>Department</FormLabel>
                            <FormControl>
                              <Input {...field} disabled={!hubSharedEnabled} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </TooltipTrigger>
                  {!hubSharedEnabled && (
                    <TooltipContent>
                      <p>Enable Hub Share to configure this setting</p>
                    </TooltipContent>
                  )}
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <FormField
                        control={form.control}
                        name="hospital"
                        render={({ field }) => (
                          <FormItem className={!hubSharedEnabled ? 'opacity-50' : ''}>
                            <FormLabel>Hospital</FormLabel>
                            <FormControl>
                              <Input {...field} disabled={!hubSharedEnabled} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </TooltipTrigger>
                  {!hubSharedEnabled && (
                    <TooltipContent>
                      <p>Enable Hub Share to configure this setting</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
